import { useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash, ArrowLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, Field, NativeSelect, Segmented, Th, Td } from "@/components/common";
import { api, money, errMsg, todayStr, VAT_RATES, qty } from "@/lib/api";
import { useLoad } from "@/lib/useLoad";
import StockShortageDialog from "@/components/StockShortageDialog";

let seq = 0;
const newLine = () => ({ key: ++seq, product_id: "", qty: 1, price: 0, discount: 0, vat_rate: 20 });
const r2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;

function calc(items, vatOn) {
  let sub = 0, vat = 0;
  const lines = items.map((l) => {
    const s = r2((Number(l.qty) || 0) * (Number(l.price) || 0) * (1 - (Number(l.discount) || 0) / 100));
    const v = vatOn ? r2((s * Number(l.vat_rate)) / 100) : 0;
    sub += s; vat += v;
    return { ...l, sub: s, vat: v, total: r2(s + v) };
  });
  return { lines, sub: r2(sub), vat: r2(vat), total: r2(sub + vat) };
}

function LineRow({ l, i, products, onChange, onPick, onRemove, vatOn, cur, short }) {
  const p = products.find((x) => x.id === l.product_id);
  return (
    <tr className="align-top">
      <Td className="min-w-[220px]">
        <NativeSelect value={l.product_id} onChange={(e) => onPick(i, e.target.value)} data-testid={`line-product-select-${i}`}>
          <option value="">Ürün seçin…</option>
          {products.map((x) => <option key={x.id} value={x.id}>{x.code} · {x.name}</option>)}
        </NativeSelect>
        {p && (
          <div className={`mt-1 text-[11px] ${short ? "font-semibold text-red-600" : "text-muted-foreground"}`} data-testid={`line-stock-info-${i}`}>
            Stok: {qty(p.stock)} {p.unit}{short && " · Stok yetersiz!"}
          </div>
        )}
      </Td>
      <Td><Input type="number" step="any" min="0" value={l.qty} onChange={(e) => onChange(i, "qty", e.target.value)} className="w-20 text-right" data-testid={`line-qty-input-${i}`} /></Td>
      <Td><Input type="number" step="0.01" min="0" value={l.price} onChange={(e) => onChange(i, "price", e.target.value)} className="w-28 text-right" data-testid={`line-price-input-${i}`} /></Td>
      <Td><Input type="number" step="any" min="0" max="100" value={l.discount} onChange={(e) => onChange(i, "discount", e.target.value)} className="w-16 text-right" data-testid={`line-discount-input-${i}`} /></Td>
      <Td>
        <NativeSelect value={l.vat_rate} disabled={!vatOn} onChange={(e) => onChange(i, "vat_rate", e.target.value)} className="w-20" data-testid={`line-vat-select-${i}`}>
          {VAT_RATES.map((v) => <option key={v} value={v}>%{v}</option>)}
        </NativeSelect>
      </Td>
      <Td right className="pt-4 font-semibold" data-testid={`line-total-${i}`}>{money(l.total, cur)}</Td>
      <Td><Button type="button" variant="ghost" size="icon" onClick={() => onRemove(i)} className="text-red-600" data-testid={`line-remove-button-${i}`}><Trash /></Button></Td>
    </tr>
  );
}

export default function DocForm({ mode }) {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const draft = useLocation().state?.draft;
  const isOrder = mode === "order";
  const base = isOrder ? "/siparisler" : "/faturalar";
  const [kind, setKind] = useState(sp.get("tur") === "purchase" ? "purchase" : "sale");
  const [h, setH] = useState({ account_id: draft?.account_id || sp.get("cari") || "", date: draft?.date || todayStr(), due_date: "", currency: draft?.currency || "TRY", rate: 1, vat_enabled: true, notes: draft?.notes || "" });
  const [items, setItems] = useState(draft?.items?.length ? draft.items.map((l) => ({ ...newLine(), ...l })) : [newLine()]);
  const [busy, setBusy] = useState(false);
  const [shortage, setShortage] = useState([]);
  const [accounts] = useLoad(() => api.get("/accounts").then((r) => r.data), []);
  const [products] = useLoad(() => api.get("/products").then((r) => r.data), []);
  const prods = products || [];
  const accs = (accounts || []).filter((a) => a.id === h.account_id || a.type === "both" || a.type === (kind === "sale" ? "customer" : "supplier"));
  const t = calc(items, h.vat_enabled);
  const rate = h.currency === "TRY" ? 1 : Number(h.rate) || 1;

  const setRate = (currency, newRate) => {
    const oldR = rate;
    const nr = currency === "TRY" ? 1 : Number(newRate) || 1;
    if (nr !== oldR) setItems(items.map((l) => (l.product_id ? { ...l, price: r2(((Number(l.price) || 0) * oldR) / nr) } : l)));
    setH({ ...h, currency, rate: currency === "TRY" ? 1 : newRate });
  };
  const change = (i, k, v) => setItems(items.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const pick = (i, pid) => {
    const p = prods.find((x) => x.id === pid);
    setItems(items.map((l, j) => (j === i ? { ...l, product_id: pid, price: p ? r2((kind === "sale" ? p.sale_price : p.purchase_price) / rate) : 0, vat_rate: p ? p.vat_rate : l.vat_rate } : l)));
  };

  const checkStock = !isOrder && kind === "sale";
  const needs = {};
  items.forEach((l) => { if (l.product_id) needs[l.product_id] = (needs[l.product_id] || 0) + (Number(l.qty) || 0); });
  const shortList = checkStock ? prods.filter((p) => needs[p.id] > p.stock).map((p) => ({ ...p, need: needs[p.id] })) : [];
  const shortIds = new Set(shortList.map((p) => p.id));

  const save = (e) => {
    e.preventDefault();
    if (!h.account_id) return toast.error("Cari hesap seçin");
    if (!items.some((l) => l.product_id)) return toast.error("En az bir ürün satırı ekleyin");
    if (shortList.length) return setShortage(shortList);
    submit();
  };

  const submit = async () => {
    setShortage([]);
    const valid = items.filter((l) => l.product_id);
    setBusy(true);
    try {
      const body = { ...h, kind, rate, due_date: h.due_date || null,
        items: valid.map((l) => ({ product_id: l.product_id, qty: Number(l.qty), price: Number(l.price), discount: Number(l.discount) || 0, vat_rate: Number(l.vat_rate) })) };
      const { data } = await api.post(isOrder ? "/orders" : "/invoices", body);
      if (draft?.receipt_file_id && !isOrder) await api.post(`/files/${draft.receipt_file_id}/attach`, { entity_type: "invoice", entity_id: data.id }).catch(() => {});
      toast.success(`${data.no} kaydedildi`);
      nav(`${base}/${data.id}`);
    } catch (e2) {
      toast.error(errMsg(e2));
    }
    setBusy(false);
  };

  const title = `Yeni ${kind === "sale" ? "Satış" : "Alış"} ${isOrder ? "Siparişi" : "Faturası"}`;
  const prefix = isOrder ? (kind === "sale" ? "SS" : "AS") : (kind === "sale" ? "SF" : "AF");
  const [numbering] = useLoad(() => api.get("/numbering").then((r) => r.data), []);
  const nextNo = numbering?.find((n) => n.prefix === prefix)?.next;
  return (
    <form onSubmit={save}>
      <Link to={base} className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="docform-back-link"><ArrowLeft size={14} /> {isOrder ? "Siparişler" : "Faturalar"}</Link>
      <PageHeader eyebrow={isOrder ? "Sipariş onaylanınca faturaya dönüşür" : "Kayıt sonrası stok ve cari bakiye güncellenir"} title={title} testid="docform-title">
        {nextNo && <span className="inline-flex items-center gap-2 border border-border bg-white px-3 py-1.5 font-num text-sm" data-testid="docform-next-no" title="Otomatik numaratör · Ayarlar'dan değiştirilebilir">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Belge No</span> {nextNo}
        </span>}
        <Segmented value={kind} onChange={(v) => { setKind(v); setH({ ...h, account_id: "" }); }} prefix="docform-kind" options={[["sale", "Satış"], ["purchase", "Alış"]]} />
      </PageHeader>
      <div className="panel mb-6 grid grid-cols-1 gap-4 p-5 sm:grid-cols-2 lg:grid-cols-6">
        <Field label="Cari Hesap *" className="lg:col-span-2">
          <NativeSelect value={h.account_id} onChange={(e) => setH({ ...h, account_id: e.target.value })} data-testid="docform-account-select">
            <option value="">Seçin…</option>
            {accs.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </NativeSelect>
        </Field>
        <Field label="Tarih"><Input type="date" required value={h.date} onChange={(e) => setH({ ...h, date: e.target.value })} data-testid="docform-date-input" /></Field>
        <Field label={isOrder ? "Teslim / Sevk Tarihi" : "Vade Tarihi"}><Input type="date" value={h.due_date} onChange={(e) => setH({ ...h, due_date: e.target.value })} data-testid="docform-due-input" /></Field>
        <Field label="Para Birimi">
          <NativeSelect value={h.currency} onChange={(e) => setRate(e.target.value, h.rate)} data-testid="docform-currency-select">
            <option value="TRY">TL ₺</option><option value="USD">USD $</option><option value="EUR">EUR €</option>
          </NativeSelect>
        </Field>
        <Field label={h.currency === "TRY" ? "Kur" : `Kur (1 ${h.currency} = ? ₺)`}>
          <Input type="number" step="0.0001" min="0.0001" disabled={h.currency === "TRY"} value={h.rate} onChange={(e) => setRate(h.currency, e.target.value)} data-testid="docform-rate-input" />
        </Field>
      </div>

      <div className="panel mb-6">
        <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
          <h3 className="font-display text-lg font-bold">Kalemler</h3>
          <label className="flex items-center gap-2 text-sm"><Switch checked={h.vat_enabled} onCheckedChange={(v) => setH({ ...h, vat_enabled: v })} data-testid="docform-vat-switch" /> KDV uygula</label>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr><Th>Ürün</Th><Th>Miktar</Th><Th>Birim Fiyat</Th><Th>İsk. %</Th><Th>KDV</Th><Th right>Tutar</Th><Th /></tr></thead>
            <tbody>
              {t.lines.map((l, i) => (
                <LineRow key={l.key} l={l} i={i} products={prods} short={shortIds.has(l.product_id)} onChange={change} onPick={pick} vatOn={h.vat_enabled} cur={h.currency}
                  onRemove={(j) => setItems(items.length > 1 ? items.filter((_, k) => k !== j) : [newLine()])} />
              ))}
            </tbody>
          </table>
        </div>
        <div className="p-4"><Button type="button" variant="outline" size="sm" onClick={() => setItems([...items, newLine()])} data-testid="docform-add-line-button"><Plus /> Satır Ekle</Button></div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Field label="Notlar" className="lg:col-span-2"><Textarea rows={4} value={h.notes} onChange={(e) => setH({ ...h, notes: e.target.value })} data-testid="docform-notes-input" /></Field>
        <div className="panel p-5 font-num text-sm" data-testid="docform-totals">
          <div className="flex justify-between py-1"><span className="text-muted-foreground">Ara Toplam</span><span data-testid="docform-subtotal">{money(t.sub, h.currency)}</span></div>
          <div className="flex justify-between py-1"><span className="text-muted-foreground">KDV</span><span data-testid="docform-vat">{money(t.vat, h.currency)}</span></div>
          <div className="mt-2 flex justify-between border-t border-border pt-3 text-lg font-semibold"><span>Genel Toplam</span><span data-testid="docform-total">{money(t.total, h.currency)}</span></div>
          {h.currency !== "TRY" && <div className="mt-1 text-right text-xs text-muted-foreground">TL karşılığı: {money(t.total * rate)}</div>}
          {shortList.length > 0 && <div className="mt-3 border border-red-200 bg-red-50 px-3 py-2 font-sans text-xs text-red-700" data-testid="docform-stock-warning">{shortList.length} üründe stok yetersiz</div>}
          <Button type="submit" disabled={busy} className="mt-5 h-10 w-full rounded-[4px]" data-testid="docform-save-button">{busy ? "Kaydediliyor…" : "Kaydet"}</Button>
        </div>
      </div>
      <StockShortageDialog items={shortage} onCancel={() => setShortage([])} onConfirm={submit} />
    </form>
  );
}
